/**
 * Service-worker registration, the prompt-to-reload update lifecycle, and the
 * page ↔ worker message channel (phase6-contract.md §3.8).
 *
 * A LEAF: it imports no service (`push.service.ts` subscribes to the worker's
 * messages through {@link onWorkerMessage}; `realtime-navigation.ts` is reached
 * by that subscriber, never from here). Keep it that way — this module is
 * evaluated before the router, the stores and the realtime service exist. Its
 * pwa-layer siblings `update-session.ts`, `hard-reload.ts` and `reload.ts` are
 * leaves too.
 *
 * Registration is deliberately gated (see {@link isPwaSupported}):
 *  - **Native builds are excluded.** `cap sync` packages the same `dist/` into
 *    the Android/iOS WebView, and `androidScheme: 'https'` makes that a secure
 *    context — so an ungated worker WOULD register inside the native app and
 *    start serving a precached shell on top of the bundled assets, which is the
 *    classic "native app won't update after release" bug.
 *  - **Dev is excluded** (the plugin also has `devOptions.enabled: false`): the
 *    dev server proxies the API paths and serves unbuilt modules over HMR.
 *  - **Desktop is excluded while `VITE_DESKTOP_APP_ENABLED` is false** (§1.1,
 *    §3.9). The mobile-only wall normally keeps a desktop browser from ever
 *    reaching this file; this is the second lock, so that a bypassed wall can
 *    never leave a worker behind on a form factor the flag has not opened.
 *
 * Update flow (`registerType: 'prompt'`): a new worker installs in the
 * background and parks in `waiting`; {@link pwaNeedRefresh} flips true and a
 * prompt's primary action calls {@link applyUpdate}. That puts up the
 * "Updating Syntry…" wall (`update-session.ts`), waits until the wall has been
 * on screen `MIN_VISIBLE_MS`, then posts SKIP_WAITING (answered by `sw.ts`).
 * Because the worker also claims clients, EVERY open tab then gets
 * `controllerchange` — "the update is installed" — and reloads exactly once
 * through {@link reloadForUpdate}, closing the version-skew window where a stale
 * tab could lazy-load a chunk hash that no longer exists on the server. That
 * reload is served by the new worker's precache, so it marks the handoff
 * `landed` and the reloaded page says "Update complete" even when the deploy
 * kept the same version/build (`update-session.ts`, "The handoff"). When
 * nothing takes control within ACTIVATION_TIMEOUT_MS the fallback reloads anyway
 * (plainly, or through `hardReload()` for a worker stuck in `waiting`), so the
 * wall can never become a dead end — those reloads land on whatever is active
 * now, so they never claim `landed`.
 *
 * The FIRST claim of a page that loaded uncontrolled — a first install, or the
 * page `hardReload()` just produced — is not an update: the page's bundle and
 * the worker's precache normally come from the same deploy. So the new
 * controller is asked which build it is, and the page reloads only when the
 * answer differs (a deploy landed between the page load and the install) or
 * never comes. Before this, every first visit reloaded itself a second or two
 * after it painted, and the reload that follows `hardReload()` cut the
 * "Update complete" toast off.
 */
import { readonly, ref, type Ref } from 'vue';
import { Capacitor } from '@capacitor/core';
import { registerSW } from 'virtual:pwa-register';
// Import-free constants module — keeps this file a leaf.
import { OFFLINE_UPDATE_QUIESCE_MAX_MS } from '@/contracts/offline-attendance-policy';
import { cleanupStaleRuntimeCaches } from '@/services/offline-cache';
import { classifyDevice } from '@/utils/device';
import { DESKTOP_APP_ENABLED } from '@/utils/env';
import { APP_BUILD, APP_VERSION } from '@/utils/app-info';
import { hardReload } from '@/pwa/hard-reload';
import { reloadPage } from '@/pwa/reload';
import {
  armUpdateStall,
  beginUpdateSession,
  isUpdateSessionActive,
  markRestarting,
  setUpdateTarget,
  stashUpdateHandoff,
  whenOverlayShown,
  type BuildId,
} from '@/pwa/update-session';

/** How often to proactively ask the server for a newer sw.js. */
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

/** After SKIP_WAITING: how long the new worker gets to take control before the fallback. */
const ACTIVATION_TIMEOUT_MS = 10_000;

/** How long the first claim of an uncontrolled page waits for the worker to name its build. */
const CLAIM_PROBE_TIMEOUT_MS = 3_000;

/** How long the wall waits for the waiting worker's version before showing none. */
const TARGET_PROBE_TIMEOUT_MS = 1_000;

const needRefresh = ref(false);
const offlineReady = ref(false);

let updateSW: ((reload?: boolean) => Promise<void>) | undefined;
let registration: ServiceWorkerRegistration | undefined;
/** Single-flight guard: every update reload in this document is issued exactly once. */
let reloading = false;
/** A worker has controlled this document — since it loaded, or since a verified first claim. */
let hasBeenControlled = false;
/** The in-flight {@link applyUpdate}: a double tap, or both prompts, run it once. */
let applying: Promise<void> | null = null;
/** What an update waits for before switching builds ({@link setUpdateQuiesce}); null = nothing. */
let quiesce: (() => Promise<void>) | null = null;

/**
 * Register what an update must wait for before the new build takes over —
 * `main.ts` passes the offline-attendance orchestrator's `offlineQuiesce()`, so
 * a punch being committed (or a sync answer being settled) is not cut off by
 * SKIP_WAITING or the reload. Injected rather than imported: this module stays
 * a leaf. The wait is bounded by `OFFLINE_UPDATE_QUIESCE_MAX_MS`; `null`
 * removes it.
 */
export function setUpdateQuiesce(fn: (() => Promise<void>) | null): void {
  quiesce = fn;
}

/** Wait for the registered quiesce, at most `OFFLINE_UPDATE_QUIESCE_MAX_MS`. Never rejects. */
async function waitForQuiesce(fn: () => Promise<void>): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, OFFLINE_UPDATE_QUIESCE_MAX_MS);
  });
  try {
    await Promise.race([
      Promise.resolve()
        .then(fn)
        .catch(() => undefined), // a quiesce that fails is a quiesce that is done
      deadline,
    ]);
  } finally {
    clearTimeout(timer);
  }
}

/** True once an updated worker is installed and waiting — show the prompt. */
export const pwaNeedRefresh: Readonly<Ref<boolean>> = readonly(needRefresh);

/** True once the first install finishes — the app shell is now offline-capable. */
export const pwaOfflineReady: Readonly<Ref<boolean>> = readonly(offlineReady);

/** Whether a service worker should be registered in this runtime at all. */
export function isPwaSupported(): boolean {
  return (
    import.meta.env.PROD &&
    !Capacitor.isNativePlatform() &&
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    (DESKTOP_APP_ENABLED || classifyDevice().formFactor !== 'desktop')
  );
}

/** True when a worker is actually controlling this page. */
export function swActive(): boolean {
  return typeof navigator !== 'undefined' && navigator.serviceWorker?.controller != null;
}

/** True when a downloaded update is parked in `waiting` — appliable even offline. */
export function hasWaitingWorker(): boolean {
  return registration?.waiting != null;
}

/**
 * THE update reload. Every path that reloads because a new build took over —
 * `controllerchange`, workbox-window's `controlling` (via `onNeedReload`), the
 * activation fallback — comes through here, so one update issues ONE reload.
 *
 * `landed` says WHY: an update worker took control of this already-controlled
 * page, so the reload it triggers is served by that worker's precache and the
 * handoff may promise a new build whatever the version pair says. The
 * fallbacks and the first-claim paths never pass it — they land on whatever
 * is active now.
 */
function reloadForUpdate(options: { landed?: boolean } = {}): void {
  if (reloading) return;
  reloading = true;
  // A tab the update was applied TO (another tab tapped Update) explains its
  // own reload after boot. Never overwrites this tab's own explicit record —
  // a landed reload only adds `landed` to it.
  if (hasBeenControlled || isUpdateSessionActive()) {
    stashUpdateHandoff({ explicit: false, landed: options.landed === true });
  }
  markRestarting();
  // A punch being committed finishes first (bounded); with nothing registered
  // the reload is immediate, as it always was.
  const fn = quiesce;
  if (fn === null) {
    reloadPage();
    return;
  }
  void waitForQuiesce(fn).then(() => reloadPage());
}

function onControllerChange(): void {
  if (hasBeenControlled) {
    // A controlled page changed controller: the update worker took over, and
    // the reload this triggers is served by that worker's precache.
    reloadForUpdate({ landed: true });
    return;
  }
  if (isUpdateSessionActive()) {
    // Uncontrolled but mid-update (the watchdog's hard path, a first visit):
    // this is the FIRST claim, by whatever worker the network handed this page
    // — not proof of a new build, so not `landed`.
    reloadForUpdate();
    return;
  }
  // The first claim of a page that loaded uncontrolled: reload only when the
  // claiming worker is a different build than the one running (see the file
  // docblock). No answer counts as different — the old, safe behaviour.
  void probeWorkerBuild(navigator.serviceWorker.controller, CLAIM_PROBE_TIMEOUT_MS).then((build) => {
    if (build && build.version === APP_VERSION && build.build === APP_BUILD) {
      hasBeenControlled = true;
      return;
    }
    reloadForUpdate();
  });
}

/** Register the service worker and start the periodic update checks. */
export function initPwa(): void {
  if (!isPwaSupported()) return;

  // Drop RUNTIME caches from a superseded build (`syntry-api-vN`). The
  // worker's own `cleanupOutdatedCaches()` covers only the precache, so this
  // boot-time sweep still stands in for an activate hook on the runtime side.
  void cleanupStaleRuntimeCaches();

  // Read BEFORE registering: whether this document started under a worker is
  // what separates an update claim from a first claim.
  hasBeenControlled = navigator.serviceWorker.controller != null;

  // A tab that did NOT confirm the update still gets claimed by the new worker;
  // reload it so every tab ends up on the same build.
  navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

  updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      needRefresh.value = true;
    },
    onOfflineReady() {
      offlineReady.value = true;
    },
    // The plugin's `controlling` listener (an update taking control) would
    // otherwise call a bare `location.reload()` BESIDE the controllerchange
    // listener above — two reload calls for one update. workbox-window forwards
    // it only with `isUpdate` — the page was controlled — so it is `landed`.
    onNeedReload: () => reloadForUpdate({ landed: true }),
    onRegisteredSW(_swScriptUrl, reg) {
      if (!reg) return;
      registration = reg;
      window.setInterval(() => void reg.update().catch(() => undefined), UPDATE_CHECK_INTERVAL_MS);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void reg.update().catch(() => undefined);
      });
    },
    onRegisterError(error) {
      console.warn('[pwa] service worker registration failed', error);
    },
  });
}

/**
 * The new worker did not take control within ACTIVATION_TIMEOUT_MS of
 * SKIP_WAITING. Nothing left waiting (it activated without claiming us, or a
 * newer one replaced it) → a plain reload lands on whatever is active now. A
 * worker still parked in `waiting` is stuck → `hardReload()` fetches the build
 * cold; if the host cannot be reached it touches nothing, and the plain reload
 * comes back on the old worker's precached shell — never an error page.
 */
function onActivationStalled(): void {
  if (reloading) return;
  if (!hasWaitingWorker()) {
    reloadForUpdate();
    return;
  }
  void hardReload({
    onCommit: () => {
      reloading = true;
      markRestarting();
    },
  }).then((outcome) => {
    if (outcome === 'unreachable') reloadForUpdate();
  });
}

/** Fill in the wall's "→ vX" from the waiting worker itself. Best effort. */
async function probeWaitingTarget(): Promise<void> {
  let reg = registration;
  if (!reg && typeof navigator !== 'undefined' && navigator.serviceWorker) {
    reg = await navigator.serviceWorker.getRegistration().catch(() => undefined);
  }
  const build = await probeWorkerBuild(reg?.waiting, TARGET_PROBE_TIMEOUT_MS);
  if (build) setUpdateTarget(build);
}

async function runUpdate(options: { target?: BuildId | null }): Promise<void> {
  needRefresh.value = false;
  beginUpdateSession({ target: options.target ?? null });
  if (!options.target) void probeWaitingTarget();

  // The wall is seen before anything is switched in (MIN_VISIBLE_MS).
  await whenOverlayShown();
  // Another tab applied the update during the dwell and this one is already on
  // its way out — posting SKIP_WAITING now would be pointless.
  if (reloading || !isUpdateSessionActive()) return;
  // A punch being committed (or a sync answer being settled) finishes before
  // the new build takes over — bounded, so an update never hangs on it.
  const fn = quiesce;
  if (fn !== null) {
    await waitForQuiesce(fn);
    if (reloading || !isUpdateSessionActive()) return;
  }

  if (!updateSW) {
    reloadForUpdate();
    return;
  }
  // This step's own fallback — `controllerchange` normally wins long before it.
  // It is load-bearing: workbox-window's `messageSkipWaiting()` silently does
  // nothing when no worker is waiting, and `updateSW()` resolves right after
  // posting either way.
  armUpdateStall(ACTIVATION_TIMEOUT_MS, onActivationStalled);
  try {
    await updateSW(true);
  } catch {
    reloadForUpdate();
  }
}

/**
 * Apply the waiting update behind the "Updating Syntry…" wall. Bound to the
 * update banner's primary action, and to the version watchdog's service-worker
 * path (which passes the version it already knows as `target`).
 *
 * Resolves once SKIP_WAITING has been posted (or a reload started); the page
 * then reloads out from under the caller. Runs once per document — a second
 * call returns the first call's promise.
 */
export function applyUpdate(options: { target?: BuildId | null } = {}): Promise<void> {
  applying ??= runUpdate(options);
  return applying;
}

export type UpdateCheckResult = 'update-available' | 'up-to-date' | 'error' | 'unsupported';

/** Explicit update check, for the About page's "Check for updates" row. */
export async function checkForUpdates(): Promise<UpdateCheckResult> {
  const reg = registration;
  if (!reg) return 'unsupported';

  try {
    await reg.update();
  } catch {
    // Offline or a transient network failure. A worker that already finished
    // downloading can still be applied without the network.
    return reg.waiting || needRefresh.value ? 'update-available' : 'error';
  }

  if (reg.waiting || needRefresh.value) return 'update-available';

  // A newer worker may still be installing — let it settle before answering.
  const installing = reg.installing;
  if (installing) {
    await new Promise<void>((resolve) => {
      const onStateChange = (): void => {
        if (installing.state === 'installed' || installing.state === 'redundant') {
          installing.removeEventListener('statechange', onStateChange);
          resolve();
        }
      };
      installing.addEventListener('statechange', onStateChange);
    });
    if (reg.waiting || needRefresh.value) return 'update-available';
  }

  return 'up-to-date';
}

/* ── Worker → page messages ─────────────────────────────────────────────────── */

/**
 * Everything `src/pwa/sw.ts` posts to a page. The worker cannot import this
 * type (it imports only `workbox-*`), so the two sides are kept in step by
 * hand — change one, change the other, and the service-worker spec.
 *
 * The other direction needs no helper: `push.service.ts` posts
 * `{type:'syntry:sw-config', vapid, tenant}` straight to the registration's
 * worker, `applyUpdate()` posts `SKIP_WAITING` through the plugin's
 * `updateSW`, and a `{type:'syntry:sw-version'}` probe
 * ({@link probeWorkerBuild}) is answered with the `syntry:sw-version` message
 * below.
 */
export type WorkerMessage =
  /** The worker skipped a push because this origin had a focused window — reconcile. */
  | { type: 'syntry:push-suppressed'; eventId: string | null }
  /** The browser rotated the push subscription and the worker re-subscribed — re-register. */
  | { type: 'syntry:push-resubscribed' }
  /** The browser rotated the push subscription and the worker could not re-subscribe — clear the local record. */
  | { type: 'syntry:push-resubscribe-failed' }
  /** A notification was tapped while a Syntry window existed — navigate in-app. */
  | { type: 'syntry:notification-click'; url: string; eventId: string | null; tenant: string }
  /**
   * The answer to a `syntry:sw-version` probe: the worker's baked-in version
   * and build. `build` is null from a worker built before it was added.
   */
  | { type: 'syntry:sw-version'; version: string; build: string | null };

type WorkerMessageHandler = (message: WorkerMessage) => void;

const handlers = new Set<WorkerMessageHandler>();
let listening = false;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Narrow an arbitrary `event.data` to a {@link WorkerMessage}, or `null`. */
export function parseWorkerMessage(data: unknown): WorkerMessage | null {
  if (!isRecord(data) || typeof data.type !== 'string') return null;
  switch (data.type) {
    case 'syntry:push-suppressed':
      return { type: data.type, eventId: typeof data.eventId === 'string' ? data.eventId : null };
    case 'syntry:push-resubscribed':
    case 'syntry:push-resubscribe-failed':
      return { type: data.type };
    case 'syntry:notification-click':
      if (typeof data.url !== 'string') return null;
      return {
        type: data.type,
        url: data.url,
        eventId: typeof data.eventId === 'string' ? data.eventId : null,
        tenant: typeof data.tenant === 'string' ? data.tenant : '',
      };
    case 'syntry:sw-version':
      return {
        type: data.type,
        version: typeof data.version === 'string' ? data.version : '',
        build: typeof data.build === 'string' && data.build !== '' ? data.build : null,
      };
    default:
      return null;
  }
}

/**
 * Ask a worker which build it is, over a private MessageChannel.
 *
 * @returns null when there is no worker, it does not answer within `timeoutMs`,
 *   or the answer is not a `syntry:sw-version` message with a version.
 */
export function probeWorkerBuild(
  worker: ServiceWorker | null | undefined,
  timeoutMs: number,
): Promise<BuildId | null> {
  if (!worker || typeof MessageChannel === 'undefined') return Promise.resolve(null);
  return new Promise<BuildId | null>((resolve) => {
    const channel = new MessageChannel();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let settled = false;
    const finish = (value: BuildId | null): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      channel.port1.onmessage = null;
      channel.port1.close();
      resolve(value);
    };
    timer = setTimeout(() => finish(null), timeoutMs);
    channel.port1.onmessage = (event: MessageEvent): void => {
      const message = parseWorkerMessage(event.data);
      finish(
        message?.type === 'syntry:sw-version' && message.version !== ''
          ? { version: message.version, build: message.build }
          : null,
      );
    };
    try {
      worker.postMessage({ type: 'syntry:sw-version' }, [channel.port2]);
    } catch {
      finish(null);
    }
  });
}

function dispatchWorkerMessage(event: MessageEvent): void {
  const message = parseWorkerMessage(event.data);
  if (!message) return;
  for (const handler of handlers) {
    try {
      handler(message);
    } catch (error) {
      console.warn('[pwa] worker message handler failed', error);
    }
  }
}

/**
 * Subscribe to the worker's messages. Returns the unsubscribe function.
 *
 * The listener sits on `navigator.serviceWorker` (present in dev too, where
 * no worker registers — the handler then simply never fires) and is attached
 * once, on the first subscriber. Messages that are not a known
 * {@link WorkerMessage} are dropped before any handler sees them.
 */
export function onWorkerMessage(handler: WorkerMessageHandler): () => void {
  handlers.add(handler);
  if (!listening && typeof navigator !== 'undefined' && navigator.serviceWorker) {
    listening = true;
    navigator.serviceWorker.addEventListener('message', dispatchWorkerMessage);
  }
  return () => {
    handlers.delete(handler);
  };
}
